import { useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { BucketBars, ShareDonut, StackByDay } from './charts';
import { useCachedState, useRefreshVersion } from './store';
import { Badge, Empty, ErrorState, Metric, Select, ago, date, duration, shortNumber, titleCase } from './ui';
import { Flag } from './Users';

const RANGES = [['7', 'Last 7 days'], ['30', 'Last 30 days'], ['90', 'Last 90 days'], ['all', 'All time']];
const PAGE_SIZE = 25;
const PACK_NAMES = { starter: 'Starter pack', creator: 'Creator pack', studio: 'Studio pack', custom: 'Custom top-up' };
const PLAN_NAMES = { pro: 'Pro', studio: 'Studio' };

/** "Studio plan", "Creator pack" — packs and plans share the id 'studio', so the product decides. */
const itemName = (product, item) => product === 'plan' ? `${PLAN_NAMES[item] ?? titleCase(item)} plan` : PACK_NAMES[item] ?? `${titleCase(item)} pack`;

/** Minor units in the currency they were charged in; nothing here converts between currencies. */
const amount = (minor, currency) => {
  const digits = Number(minor) % 100 ? 2 : 0;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: digits, maximumFractionDigits: digits }).format(Number(minor ?? 0) / 100);
};
const amounts = (rows = []) => rows.length ? rows.map((r) => amount(r.amountMinor, r.currency)).join(' · ') : '—';

const OUTCOMES = {
  paid: ['Paid', 'good', 'var(--chart-done)'],
  unpaid: ['Not paid', 'warn', 'var(--chart-rendering)'],
  failed: ['Failed', 'danger', 'var(--chart-error)'],
};
const OUTCOME_SERIES = Object.entries(OUTCOMES).map(([key, [label, , color]]) => ({ key, label, color }));
const SOURCES = { one_time: 'One-time', autopay_charge: 'Autopay charge', autopay_setup: 'Autopay setup' };
const AUTOPAY_TONES = { authenticated: 'good', active: 'good', pending: 'warn', halted: 'danger', failed: 'danger', created: 'muted' };
const AUTOPAY_LABELS = { created: 'Not authorised', authenticated: 'Authorised', halted: 'Halted' };

/** Same convention as Videos: a preset range is computed at request time. */
const rangeQuery = (range) => ({
  from: new Date(range === 'all' ? '2024-01-01T00:00:00Z' : Date.now() - Number(range) * 24 * 60 * 60 * 1000).toISOString(),
  to: new Date().toISOString(),
});

/**
 * The payment funnel: everyone who clicked pay, who finished, and what came
 * in. A click is recorded server-side before Razorpay's checkout opens, so a
 * "Not paid" row is a customer who reached the payment screen and left.
 */
export function Payments() {
  const [range, setRange] = useState('30');
  const query = useMemo(() => rangeQuery(range), [range]);
  const statsKey = `/payments/stats?${new URLSearchParams(query)}`;
  const [stats, setStats] = useCachedState(statsKey);
  const [loading, setLoading] = useState(!stats);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const refreshVersion = useRefreshVersion();

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    api(statsKey).then((next) => { if (!cancelled) setStats(next); })
      .catch((caught) => { if (!cancelled) setError(caught.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [statsKey, refreshVersion, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const c = stats?.clicks; const revenue = stats?.revenue ?? []; const autopay = stats?.autopay;
  const conversion = c?.total ? Math.round((c.paid / c.total) * 100) : 0;
  const payments = revenue.reduce((sum, r) => sum + r.payments, 0);
  const autopayPayments = revenue.reduce((sum, r) => sum + r.autopayPayments, 0);
  const outcomeSlices = useMemo(() => OUTCOME_SERIES.map((s) => ({ ...s, title: s.label, value: c?.[s.key] ?? 0 })), [c]);
  const productRows = useMemo(() => (stats?.products ?? []).map((p) => ({
    key: `${p.product}-${p.item}`, label: itemName(p.product, p.item),
    title: `${itemName(p.product, p.item)} — ${p.paid} of ${p.clicks} paid`, value: p.clicks,
  })), [stats]);

  return <>
    <section className="page-title"><div><div className="eyebrow">Payments</div><h1>Payments</h1><p>Everyone who clicked pay, who finished paying, and what came in — credit packs, plans and autopay.</p></div>
      <div className="btn-row"><Select value={range} onChange={(e) => setRange(e.target.value)} options={RANGES} label="Date range" /></div></section>

    {error && <ErrorState message={error} retry={() => setAttempt((n) => n + 1)} />}

    <section className={`metric-grid ${loading ? 'is-loading' : ''}`}>
      <Metric label="Clicked pay" value={shortNumber(c?.total)} detail={`${c?.customers ?? 0} customer${c?.customers === 1 ? '' : 's'} opened a checkout`} />
      <Metric label="Paid" value={shortNumber(c?.paid)} detail={`${c?.paidCustomers ?? 0} customer${c?.paidCustomers === 1 ? '' : 's'} · ${conversion}% of clicks converted`} />
      <Metric label="Did not pay" value={shortNumber(c?.unpaid)} detail={`${c?.unpaidCustomers ?? 0} customer${c?.unpaidCustomers === 1 ? '' : 's'} paid nothing${c?.failed ? ` · ${c.failed} failed` : ''}`} />
      <Metric label="Revenue" value={revenue.length ? amount(revenue[0].amountMinor, revenue[0].currency) : '—'} detail={`${revenue.length > 1 ? `${amounts(revenue.slice(1))} · ` : ''}${payments} payment${payments === 1 ? '' : 's'}${autopayPayments ? `, ${autopayPayments} by autopay` : ''}`} />
      <Metric label="Autopay running" value={shortNumber(autopay?.running)} detail={`${autopay?.atRisk ?? 0} pending or halted · ${autopay?.cancelled30d ?? 0} cancelled in 30 days`} alert={(autopay?.atRisk ?? 0) > 0} />
    </section>

    <section className={`chart-grid ${loading ? 'is-loading' : ''}`}>
      <article className="panel"><div className="panel-heading"><div><span className="eyebrow">FUNNEL</span><h2>Clicks on pay per day</h2></div></div>
        {stats?.daily?.length ? <StackByDay rows={stats.daily} series={OUTCOME_SERIES} tooltip={(d) => <>
          {OUTCOME_SERIES.map((s) => <span key={s.key}><i style={{ background: s.color }} />{s.label}: {d[s.key]}</span>)}</>} /> : <Empty title="No checkouts in this range" body="Widen the date range." />}
      </article>
      <article className="panel"><div className="panel-heading"><div><span className="eyebrow">OUTCOME</span><h2>How checkouts ended</h2></div></div>
        <ShareDonut slices={outcomeSlices} total={c?.total ?? 0} unit="clicks" />
      </article>
    </section>

    <section className={`chart-grid ${loading ? 'is-loading' : ''}`}>
      <article className="panel"><div className="panel-heading"><div><span className="eyebrow">PRODUCTS</span><h2>What customers tried to buy</h2></div><small className="plain">Hover a bar for how many paid</small></div>
        {productRows.length ? <BucketBars rows={productRows} unit="clicks" /> : <Empty title="No checkouts in this range" body="Widen the date range." />}
      </article>
      <article className="panel table-panel"><div className="panel-heading"><div><span className="eyebrow">REVENUE</span><h2>Money in, by currency</h2></div></div>
        <div className="table-wrap"><table><thead><tr><th>Currency</th><th>Received</th><th>Plans</th><th>Credits</th></tr></thead><tbody>
          {revenue.length ? revenue.map((r) => <tr key={r.currency}>
            <td><b>{r.currency}</b><small>{r.payments} payment{r.payments === 1 ? '' : 's'}</small></td>
            <td><b>{amount(r.amountMinor, r.currency)}</b></td>
            <td>{amount(r.planMinor, r.currency)}</td>
            <td>{amount(r.creditsMinor, r.currency)}</td>
          </tr>) : <tr><td colSpan="4"><Empty title="Nothing received in this range" body="Payments appear here once Razorpay confirms them." /></td></tr>}
        </tbody></table></div>
      </article>
    </section>

    <Activity query={query} />
  </>;
}

const EMPTY_FILTERS = { search: '', outcome: '', product: '', source: '', status: '' };
const SEARCH_HINTS = { checkouts: 'Search name, email, order or payment id', customers: 'Search name or email', autopay: 'Search name, email or subscription id' };

/** Checkouts, customers and autopay mandates, sharing one filter bar. */
function Activity({ query }) {
  const [tab, setTab] = useState('checkouts');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [customer, setCustomer] = useState(null);
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const refreshVersion = useRefreshVersion();

  useEffect(() => {
    const timer = setTimeout(() => { setApplied(filters); setPage(0); }, filters.search === applied.search ? 0 : 350);
    return () => clearTimeout(timer);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(0); }, [customer, query, tab]);

  // Each tab has its own filters; a value from another tab would be refused.
  const switchTab = (next) => { setTab(next); setFilters((f) => ({ ...EMPTY_FILTERS, search: f.search })); setApplied((f) => ({ ...EMPTY_FILTERS, search: f.search })); };
  const openCustomer = (row) => { setCustomer({ id: row.user_id, email: row.email }); switchTab('checkouts'); };

  const path = useMemo(() => {
    const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
    if (tab !== 'autopay') Object.assign(params, query);
    if (applied.search.trim()) params.search = applied.search.trim();
    if (applied.outcome) params.outcome = applied.outcome;
    if (applied.product && tab !== 'customers') params.product = applied.product;
    if (tab === 'checkouts') {
      if (applied.source) params.source = applied.source;
      if (customer) params.userId = customer.id;
    }
    if (tab === 'autopay' && applied.status) params.status = applied.status;
    return `${tab === 'checkouts' ? '/payments' : `/payments/${tab}`}?${new URLSearchParams(params)}`;
  }, [query, page, applied, customer, tab]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    api(path).then((next) => { if (!cancelled) setData(next); })
      .catch((caught) => { if (!cancelled) setError(caught.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [path, refreshVersion]);

  const set = (key) => (event) => setFilters((f) => ({ ...f, [key]: event.target.value }));
  const total = data?.page?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // The table follows the response, not the tab, so a slow answer never lands in the wrong columns.
  const showing = path.startsWith('/payments/customers') ? 'customers' : path.startsWith('/payments/autopay') ? 'autopay' : 'checkouts';
  const heading = { checkouts: `checkout${total === 1 ? '' : 's'}`, customers: `customer${total === 1 ? '' : 's'}`, autopay: `autopay mandate${total === 1 ? '' : 's'}` }[tab];

  return <>
    <section className="toolbar">
      <div className="segmented">{[['checkouts', 'Checkouts'], ['customers', 'Customers'], ['autopay', 'Autopay']].map(([id, label]) => <button key={id} className={tab === id ? 'on' : ''} onClick={() => switchTab(id)}>{label}</button>)}</div>
      <input value={filters.search} onChange={set('search')} placeholder={SEARCH_HINTS[tab]} />
      {tab === 'checkouts' && <Select value={filters.outcome} onChange={set('outcome')} label="Outcome" options={[['', 'Any outcome'], ['paid', 'Paid'], ['unpaid', 'Not paid'], ['failed', 'Failed']]} />}
      {tab === 'customers' && <Select value={filters.outcome} onChange={set('outcome')} label="Outcome" options={[['', 'Everyone who clicked'], ['paid', 'Paid in this range'], ['unpaid', 'Clicked, paid nothing']]} />}
      {tab === 'autopay' && <Select value={filters.status} onChange={set('status')} label="Status" options={[['', 'Any status'], ['active', 'Active'], ['authenticated', 'Authorised'], ['created', 'Not authorised'], ['pending', 'Pending'], ['halted', 'Halted'], ['cancelled', 'Cancelled'], ['completed', 'Completed'], ['expired', 'Expired'], ['failed', 'Failed']]} />}
      {tab !== 'customers' && <Select value={filters.product} onChange={set('product')} label="Product" options={[['', 'Packs and plans'], ['credits', 'Credit packs'], ['plan', 'Plans']]} />}
      {tab === 'checkouts' && <Select value={filters.source} onChange={set('source')} label="Source" options={[['', 'One-time and autopay'], ['one_time', 'One-time'], ['autopay', 'Autopay']]} />}
      {tab === 'checkouts' && customer && <button className="subtle" onClick={() => setCustomer(null)} title="Show every customer again">{customer.email} ✕</button>}
    </section>

    {error && <ErrorState message={error} retry={() => setApplied({ ...applied })} />}

    <article className={`panel table-panel payment-users ${loading ? 'is-loading' : ''}`}>
      <div className="panel-heading"><div><span className="eyebrow">{tab === 'autopay' ? 'AUTOPAY · ANY DATE' : tab.toUpperCase()}</span><h2>{total} {heading}</h2></div>
        <div className="pager"><button className="subtle" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>← Prev</button><span>Page {page + 1} of {pages}</span><button className="subtle" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>Next →</button></div></div>
      {showing === 'customers' ? <CustomerTable items={data?.items} onOpen={openCustomer} />
        : showing === 'autopay' ? <AutopayTable items={data?.items} />
        : <CheckoutTable items={data?.items} />}
    </article>
  </>;
}

const Who = ({ name, email, country }) => <td><b>{name || email}</b><small className="plain country-line">{name ? email : ''}{country && <Flag code={country} />}</small></td>;

/** Why a row that is not paid is not paid, in words. */
const outcomeNote = (p) => {
  if (p.outcome === 'paid') return p.source === 'one_time' && p.paid_at ? `took ${duration(new Date(p.paid_at) - new Date(p.created_at))}` : '';
  if (p.source === 'autopay_setup') return p.provider_status === 'created' ? 'Mandate never authorised' : `Mandate ${AUTOPAY_LABELS[p.provider_status]?.toLowerCase() ?? p.provider_status}, no charge yet`;
  if (p.outcome === 'failed') return p.provider_order_id ? 'Payment failed' : 'Razorpay order could not be created';
  return p.provider_status === 'abandoned' ? 'Checkout abandoned' : `Checkout opened ${ago(p.created_at)}`;
};

function CheckoutTable({ items }) {
  return <div className="table-wrap"><table><thead><tr><th>Customer</th><th>What</th><th>Amount</th><th>Outcome</th><th>Source</th><th>Razorpay</th><th>Clicked</th><th>Paid</th></tr></thead><tbody>
    {items?.length ? items.map((p) => { const [label, tone] = OUTCOMES[p.outcome] ?? [titleCase(p.outcome), 'muted']; return <tr key={`${p.source}-${p.id}`}>
      <Who name={p.user_name} email={p.user_email} country={p.user_country} />
      <td><b>{itemName(p.product, p.item)}</b><small>{p.credits ? `${shortNumber(p.credits)} credits` : ''}</small></td>
      <td><b>{amount(p.amount_minor, p.currency)}</b></td>
      <td><Badge tone={tone}>{label}</Badge><small>{outcomeNote(p)}</small>
        {p.issue_bonus_credits && <small className="plain">Payment issue · {p.issue_bonus_credits} bonus credits granted</small>}</td>
      <td><Badge tone={p.source === 'one_time' ? 'muted' : 'info'}>{SOURCES[p.source] ?? p.source}</Badge></td>
      <td>{p.provider_order_id ? <><small title={p.provider_order_id}>{p.provider_order_id}</small>{p.provider_payment_id && p.provider_payment_id !== p.provider_order_id && <small title={p.provider_payment_id}>{p.provider_payment_id}</small>}</> : <span className="unassigned">—</span>}</td>
      <td>{date(p.created_at)}</td>
      <td>{p.paid_at ? date(p.paid_at) : '—'}</td>
    </tr>; }) : <tr><td colSpan="8"><Empty title="No checkouts match" body="Try clearing a filter or widening the date range." /></td></tr>}
  </tbody></table></div>;
}

function CustomerTable({ items, onOpen }) {
  return <div className="table-wrap"><table><thead><tr><th>Customer</th><th>Status</th><th>Plan now</th><th>Clicked pay</th><th>Tried to buy</th><th>Spent, all time</th><th>Last click</th></tr></thead><tbody>
    {items?.length ? items.map((u) => <tr key={u.user_id}>
      <td><button className="text-button video-link" title="Show this customer's checkouts" onClick={() => onOpen(u)}><b>{u.full_name || u.email}</b></button>
        <small className="plain country-line">{u.full_name ? u.email : ''}{u.country && <Flag code={u.country} />}</small></td>
      <td>{u.paid > 0 ? <Badge tone="good">Paid</Badge> : u.lifetime_spent?.length ? <Badge tone="info" title="Paid nothing in this range, but has paid before">Paid before</Badge> : <Badge tone="warn">Never paid</Badge>}</td>
      <td>{u.plan_id ? <Badge tone={u.plan_id === 'studio' ? 'info' : 'good'}>{PLAN_NAMES[u.plan_id] ?? u.plan_id}</Badge> : <Badge>Free</Badge>}</td>
      <td><b>{u.checkouts}</b><small>{[u.paid && `${u.paid} paid`, u.unpaid && `${u.unpaid} not paid`, u.failed && `${u.failed} failed`].filter(Boolean).join(' · ')}</small></td>
      <td>{(u.items ?? []).map((key) => { const [product, item] = key.split(':'); return itemName(product, item); }).join(', ')}</td>
      <td>{u.lifetime_spent?.length ? <b>{amounts(u.lifetime_spent)}</b> : <span className="unassigned">Nothing yet</span>}</td>
      <td>{ago(u.last_at)}<small>Joined {ago(u.signed_up_at)}</small></td>
    </tr>) : <tr><td colSpan="7"><Empty title="Nobody clicked pay in this range" body="Try clearing a filter or widening the date range." /></td></tr>}
  </tbody></table></div>;
}

function AutopayTable({ items }) {
  return <div className="table-wrap"><table><thead><tr><th>Customer</th><th>What</th><th>Each charge</th><th>Status</th><th>Charges</th><th>Next charge</th><th>Set up</th></tr></thead><tbody>
    {items?.length ? items.map((r) => <tr key={r.id}>
      <Who name={r.user_name} email={r.user_email} country={r.user_country} />
      <td><b>{itemName(r.product, r.item)}</b><small>{r.credits ? `${shortNumber(r.credits)} credits a charge` : ''}</small></td>
      <td><b>{amount(r.amount_minor, r.currency)}</b></td>
      <td><Badge tone={AUTOPAY_TONES[r.status] ?? 'muted'}>{AUTOPAY_LABELS[r.status] ?? titleCase(r.status)}</Badge>
        {r.cancelled_at && <small>Cancelled {date(r.cancelled_at)}</small>}
        {r.provider_subscription_id && <small title={r.provider_subscription_id}>{r.provider_subscription_id}</small>}</td>
      <td><b>{r.paid_count}</b></td>
      <td>{r.charge_at ? date(r.charge_at) : '—'}{r.current_end && <small>Period ends {date(r.current_end)}</small>}</td>
      <td>{date(r.created_at)}</td>
    </tr>) : <tr><td colSpan="7"><Empty title="No autopay mandates match" body="Try clearing a filter." /></td></tr>}
  </tbody></table></div>;
}
